/* eslint-disable sonarjs/no-duplicate-string */
import { FC } from "react";

import { Stack, Theme, SxProps, useTheme } from "@mui/material";
import { AvailablePaymentCurrency, formatNarrowCurrencyAmount, getDollarValue } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormAvatar } from "@/forms-design-system/FormAvatar";
import { FormLinkBlock } from "@/forms-design-system/FormLinkBlock";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { mergeSx } from "@/forms-design-system/helpers";
import { useTranslate } from "@/hooks/useTranslate";

const PREFIX = "THEMED_FUNDRAISER";

const classes = {
  subtitle: `${PREFIX}-subtitle`,
  totalRaised: `${PREFIX}-totalRaised`,
  separator: `${PREFIX}-separator`,
};

export type ThemedFundraiserProps = {
  href: string;
  imageSrc: string | null;
  title: string;
  subtitle: string;
  totalRaised?: number;
  currency: AvailablePaymentCurrency;
  showFallbackImage?: boolean;
  "data-test"?: string;
  sx?: SxProps<Theme>;
};

export const ThemedFundraiser: FC<ThemedFundraiserProps> = ({
  href,
  imageSrc,
  title,
  subtitle,
  totalRaised,
  currency,
  showFallbackImage = false,
  "data-test": dataTest,
  sx,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { isoLocale } = useLocaleContext();
  const hasTotalRaised = totalRaised !== undefined && !!currency;

  const formattedTotalRaised = hasTotalRaised
    ? formatNarrowCurrencyAmount(getDollarValue(totalRaised), {
        locale: isoLocale,
        currency,
        withCents: totalRaised % 100 !== 0,
      })
    : null;

  const styles = mergeSx(
    {
      width: "100%",
      [`&:hover`]: {
        [`& .${classes.subtitle}, & .${classes.totalRaised}, & .${classes.separator}`]: {
          color: theme.palette.forms.text.brand,
        },
      },
    },
    sx
  );

  return (
    <FormLinkBlock href={href} data-test={dataTest} sx={styles}>
      {(imageSrc || showFallbackImage) && (
        <FormAvatar src={imageSrc ?? undefined} color={theme.palette.surface.form.quiet} showFallbackIcon />
      )}
      <Stack flexGrow={1} minWidth={0}>
        <FormTypography
          variant="body2"
          vibe="moderate"
          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {title}
        </FormTypography>
        <Stack
          direction="row"
          gap={0.5}
          alignItems="center"
          minWidth={0}
          divider={
            <FormTypography
              className={classes.separator}
              variant="caption"
              vibe="supershy"
              sx={{ transition: "color 200ms ease" }}
            >
              ·
            </FormTypography>
          }
        >
          <FormTypography
            className={classes.subtitle}
            flex="0 1 auto"
            variant="caption"
            vibe="supershy"
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", transition: "color 200ms ease" }}
          >
            {subtitle}
          </FormTypography>
          {totalRaised !== undefined && (
            <FormTypography
              className={classes.totalRaised}
              flexShrink={0}
              variant="caption"
              vibe="supershy"
              sx={{ whiteSpace: "nowrap", transition: "color 200ms ease" }}
            >
              {t("donationForm", "peerToPeer.totalRaised", {
                totalRaised: formattedTotalRaised,
              })}
            </FormTypography>
          )}
        </Stack>
      </Stack>
    </FormLinkBlock>
  );
};
